/**
 * CapBlockerModal — Phase 7 / Slot Model (07-04-PLAN).
 *
 * Surfaces when a Free user attempts to create a puzzle beyond the 3-slot
 * limit (either client-predicted via canCreatePuzzle OR server-caught via the
 * permission-denied catch in puzzleService.createPuzzle).
 *
 * Two CTAs (locked decision §2 — slot model):
 *   - "Upgrade to Pro" (primary) → mounts PaywallModal as a child overlay
 *   - "Go to My Puzzles" (secondary) → fires onGoToHistory prop so the user
 *     can delete an existing puzzle to make room
 *
 * The per-extra-puzzle IAP path is retired in Phase 7.
 *
 * Props:
 *   - open (bool)          : controls visibility
 *   - onClose (fn)         : close handler (backdrop / ✕ / ESC)
 *   - onGoToHistory (fn)   : navigates to "history" screen (My Puzzles)
 *   - onUpgrade (fn)       : (optional) override Pro CTA target. Defaults to
 *                            mounting the PaywallModal child overlay.
 *   - profile (object)     : users/{uid} snapshot. Used to derive slot state.
 *   - trigger (string)     : telemetry tag forwarded to the child PaywallModal
 *                            (typically "cap-hit").
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PaywallModal from "./PaywallModal";
import { getSlotState } from "../services/capService";

export default function CapBlockerModal({
  open,
  onClose,
  onGoToHistory,
  onUpgrade,
  profile,
  trigger = "cap-hit",
}) {
  const { t } = useTranslation();
  const [paywallOpen, setPaywallOpen] = useState(false);

  // ESC dismiss (consistent with PaywallModal). Inner guard skips when
  // closed (rules-of-hooks: effect must always run).
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onClose]);

  if (!open) return null;

  // Slot state derived from profile — pure, no time-based reset.
  const { used, limit } = getSlotState(profile);

  // Copy contract (verbatim from 07-UI-SPEC.md Copywriting Contract).
  const title = t("ui.capBlocker.title");
  // Grandfathered-over-limit: used > limit (profile has more than new limit allows).
  const body =
    used > limit
      ? t("ui.capBlocker.bodyOverLimit")
      : t("ui.capBlocker.body", { limit });

  // ── Inline styles (candy_heart, no-line rule) ──
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 320,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    position: "relative",
  };
  const closeBtnStyle = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "var(--color-bgAlt)",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--color-textMuted)",
    fontSize: 16,
    fontWeight: 700,
  };
  const iconWrapStyle = {
    width: 64,
    height: 64,
    borderRadius: 20,
    margin: "0 auto 16px",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    boxShadow: "0 8px 24px var(--color-glow)",
  };
  const titleStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    fontWeight: 800,
    color: "var(--color-text)",
    margin: "0 0 8px",
    textAlign: "center",
  };
  const bodyStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 14,
    color: "var(--color-textMuted)",
    textAlign: "center",
    margin: "0 0 24px",
    lineHeight: 1.5,
  };
  const ctaPrimary = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 8px 24px var(--color-glow)",
  };
  const ctaSecondary = {
    ...ctaPrimary,
    background: "var(--color-bgAlt)",
    color: "var(--color-text)",
    boxShadow: "none",
  };

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setPaywallOpen(true);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={overlayStyle}
        data-testid="cap-blocker-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={cardStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cap-blocker-title"
        >
          <button
            type="button"
            onClick={onClose}
            style={closeBtnStyle}
            aria-label={t("ui.capBlocker.closeAria")}
          >
            ✕
          </button>
          <div style={iconWrapStyle} aria-hidden="true">
            🧩
          </div>
          <h2 id="cap-blocker-title" style={titleStyle}>
            {title}
          </h2>
          <p style={bodyStyle}>
            {body}
          </p>

          <div
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <button
              type="button"
              onClick={handleUpgrade}
              style={ctaPrimary}
              data-testid="cap-blocker-upgrade"
            >
              {t("ui.capBlocker.upgrade")}
            </button>
            <button
              type="button"
              onClick={onGoToHistory}
              style={ctaSecondary}
              data-testid="cap-blocker-go-to-history"
            >
              {t("ui.capBlocker.goToPuzzles")}
            </button>
          </div>
        </div>
      </div>

      {/* Child PaywallModal — the DEFAULT Upgrade path. Only mounted when the
          parent has NOT supplied an override onUpgrade; with an override,
          handleUpgrade never sets paywallOpen, so this child would be dead
          wiring (IN-03). No other state depends on it always mounting. */}
      {!onUpgrade && (
        <PaywallModal
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          trigger={trigger}
        />
      )}
    </>
  );
}
